var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Third-party
import EventEmitter3 from 'eventemitter3';
import analytics from './Analytics';
import ViewerStore from './store';
import checkTracksProcessed from './util/pollActivities';
// Modules
import AvatarCollection from './Avatar';
import Input from './Input';
import CameraSystem, { IntroCam } from './Camera';
import Clock from './Clock';
import DistanceMarkers from './DistanceMarkers';
import EventCollection from './Event';
import HeightSampler from './HeightSampling';
import { Hud } from './Hud';
import Labels from './Labels';
import Loading from './Loading';
import Stats from './Stats';
import Course from './Course';
import MediaCollection from './MediaCollection';
import TerrainProfile from './TerrainProfile';
import TimeWarp from './TimeWarp';
import TrackCollection from './Track';
import TrackLoader from './TrackLoader';
import TrailCollection from './TrailCollection';
import { SegmentCollection } from './Segments';
import { PlayheadCollection } from './Playhead';
import UI from './UI';
// Helpers
import { defaultValue, isValidUrl } from './helpers';
import * as Tracking from './helpers/tracking';
// @TODO: Cleanup all imports below this
import Scene from './classes/scene';
import { setTime, setClockSpeed, setActiveActivity, setMessage, addSegment, addActivity, startPlaying, toggleControls, } from './Actions';
// Constants
const DELAY_EXIT_MS = 1500;
const DELAY_ACTIVE_END_MS = 2000;
// Globals
let app;
export function getApp() {
    return app;
}
global.analytics = analytics;
// App Class
export default function App() {
    console.assert(!app, 'App can only be created once');
    app = this;
}
App.prototype.initialize = function (rootElement, assetPath, sceneJson, options = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        // Initialize the Redux store
        this.store = new ViewerStore();
        // Parse options
        this.options = {};
        this.features = defaultValue(options.features, {});
        this.options.debugEnabled = defaultValue(options.debugEnabled, false);
        this.options.isEmbed = defaultValue(options.isEmbed, false);
        this.options.headless = defaultValue(options.headless, false);
        this.options.startWhenReady = defaultValue(options.startWhenReady, true);
        this.options.disableIntro = defaultValue(options.disableIntro, false);
        this.options.enableCaptureCam = defaultValue(options.enableCaptureCam, false);
        this.options.tileQualityLoading = defaultValue(options.tileQualityLoading, undefined);
        this.options.tileQualityPlaying = defaultValue(options.tileQualityLoading, undefined);
        this.options.isTesting = defaultValue(options.testing, false);
        this.options.hideElements = defaultValue(options.hideElements, []);
        // @NOTE: Both globes may be enabled, the canvases just overlay each other
        this.options.enableAyvriGlobe = defaultValue(options.enableAyvriGlobe, false);
        this.options.enableCesiumGlobe = defaultValue(options.enableCesiumGlobe, true);
        this.options.units = defaultValue(options.units, undefined);
        this.options.keyMap = defaultValue(options.keyMap, 'default');
        this.store.dispatch({ type: 'SET_VIEWER_OPTIONS', options: this.options });
        // Set up a global asset path that will be used as the base URL to fetch any files
        if (isValidUrl(assetPath)) {
            window.AyvriViewerAssetPath = assetPath;
        }
        else {
            console.error('Viewer requires a valid asset path. It must begin with the "http" or "https" protocol.');
            this.store.dispatch({ type: 'FATAL_ERROR' });
        }
        // External parties (like Studio or embed parent) can listen to events via the on*() functions in the Interface file
        this.eventEmitter = new EventEmitter3();
        // Construct modules
        this.ui = new UI();
        this.input = new Input();
        this.clock = new Clock();
        this.timeWarp = new TimeWarp();
        this.camera = new CameraSystem();
        this.loading = new Loading();
        this.tracks = new TrackCollection();
        this.trackLoader = new TrackLoader(defaultValue(this.features.abf_3_1_enabled, true));
        this.segments = new SegmentCollection();
        this.playheads = new PlayheadCollection(this.store);
        this.stats = new Stats(this.store);
        this.course = new Course();
        this.terrainProfile = new TerrainProfile();
        this.trails = new TrailCollection();
        this.events = new EventCollection();
        this.hud = new Hud();
        this.labels = new Labels();
        this.avatars = new AvatarCollection(this.hud, this.playheads);
        this.distanceMarkers = new DistanceMarkers();
        this.heightSampler = new HeightSampler();
        this.media = new MediaCollection(this);
        this.globes = [];
        // Early out for tests
        if (this.options.isTesting)
            return true;
        // Fill out the DOM from the rootElement down
        if (!this.options.headless)
            this.ui.initialize(rootElement, this.store);
        // Determine if WebGL is available. This is used by React components to dispay an error message if necessary.
        if (!this.options.headless) {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (!gl || !(gl instanceof WebGLRenderingContext)) {
                analytics.event({
                    category: 'Errors',
                    action: 'WebGL Unavailable',
                });
                this.store.dispatch({ type: 'FATAL_ERROR', error: 'WEBGL_UNAVAILABLE' });
                return false;
            }
        }
        // Conditionally import one or more of the Globes
        let globePromises = [];
        if (this.options.enableCesiumGlobe)
            globePromises.push(import(/* webpackChunkName: "cesium-globe" */ 'cesium-globe'));
        // Once the Globe bundles are available, initialize them
        const globeEnv = {
            bingApiKey: process.env.bingApiKey,
            bingBackupApiKey: process.env.bingBackupApiKey,
            assetPath,
        };
        globePromises = globePromises.map(p => p.then(({ default: Globe }) => __awaiter(this, void 0, void 0, function* () {
            const globe = new Globe();
            this.globes.push(globe);
            yield globe.initialize(this.options, globeEnv, this.ui, this.store);
            return globe;
        })));
        // If any globe fails to load it is a fatal error
        try {
            yield Promise.all(globePromises);
            this.globe = this.globes[0];
        }
        catch (error) {
            console.error('Failed to initialize Globe:', error);
            this.store.dispatch({ type: 'FATAL_ERROR' });
            return false;
        }
        // Load the scene asynchronously
        Promise.resolve(sceneJson)
            // Ensure that all the tracks in this scene have been processed by the backend
            .then(scene => checkTracksProcessed(scene.activities, this.store))
            // Load the scene once the activities are ready
            .then(processedActivities => {
            if (sceneJson.activities.length > 0 && processedActivities.length <= 0) {
                const e = new Error('No tracks have been successfully processed');
                console.error('Track processing failed:', e);
                this.store.dispatch({ type: 'FATAL_ERROR', error: 'TRACK_PROCESS_FAILURE' });
                e.handled = true;
                throw e;
            }
            sceneJson.activities = processedActivities;
            const newScene = new Scene(sceneJson, this.options);
            newScene.errors.forEach(e => this.store.dispatch(setMessage(e)));
            this.store.dispatch({ type: 'INITIALIZE_SCENE', scene: newScene });
            this.onSceneLoad(newScene);
        })
            // Any errors at this point are fatal
            .catch(e => {
            if (!e.handled) {
                console.error('Scene failed to load due to error:', e);
                this.store.dispatch({ type: 'FATAL_ERROR', error: 'SCENE_LOAD_FAILURE' });
            }
        });
        return true;
    });
};
App.prototype.terminate = function () {
    app = null;
};
App.prototype.onSceneLoad = function (scene) {
    const dispatch = this.store.dispatch;
    // a live track without a startpoint time, so set it to now
    if (scene.live && scene.startPoint && !scene.startPoint.time) {
        scene.startPoint.time = Date.now();
    }
    // Initialize modules
    this.globes.forEach(g => g.onSceneLoad(scene, dispatch));
    this.hud.onSceneLoad(this.ui, this.globes);
    this.input.onSceneLoad(this.ui, this);
    this.segments.onSceneLoad(scene, dispatch);
    this.heightSampler.onSceneLoad(this.globe);
    this.camera.onSceneLoad(scene, dispatch, this.input, this.options, this.heightSampler);
    this.clock.onSceneLoad(scene, dispatch);
    this.terrainProfile.onSceneLoad(scene, this.features);
    this.tracks.onSceneLoad(scene, dispatch);
    this.trackLoader.onSceneLoad(scene, dispatch);
    this.course.onSceneLoad(this, this.store, scene, dispatch, this.trackLoader, this.tracks);
    this.distanceMarkers.onSceneLoad(this.globe, scene);
    this.trails.onSceneLoad(this.globes);
    this.media.onSceneLoad(scene);
    this.labels.onSceneLoad(scene, this.globes, this.hud);
    // Add each activity and avatar
    Object.keys(scene.activities).forEach((key, _, keys) => {
        const activity = scene.activities[key];
        activity.activityId = key.toString();
        dispatch(addActivity(activity, keys.length));
        // Track the loading status of this activity
        const progress = 0.0;
        const bytesDownloaded = 0.0;
        const trackId = activity.activityId;
        dispatch({ type: 'UPDATE_TRACK_LOADING_PROGRESS', trackId, progress, bytesDownloaded });
        const readyTime = this.store.getState().activities[key].startTime - scene.startPoint.time;
        dispatch({ type: 'SET_TRACK_READY_TIME', trackId, readyTime });
    });
    // Set up the intro
    if (!this.options.disableIntro) {
        const kIntroDuration = 10000;
        const introStartTime = scene.startPoint.time - kIntroDuration * scene.sceneSettings.speed;
        dispatch(addSegment({
            time: introStartTime,
            target: 'activity',
            targetId: this.store.getState().tracks.activeTrack,
            speed: this.store.getState().scene.sceneSettings.speed,
            targetDistance: this.store.getState().scene.sceneSettings.targetDistance,
        }));
        dispatch(setTime(introStartTime));
        this.camera.addCamera(new IntroCam(), {
            duration: kIntroDuration,
            startPoint: scene.startPoint,
        });
    }
    dispatch({ type: 'UPDATE_LOADING_PROGRESS', key: 'scene', progress: 1.0 });
    this.globe.readyPromise.then(() => {
        dispatch({ type: 'UPDATE_LOADING_PROGRESS', key: 'globeReady', progress: 1.0 });
    });
    if (this.features.track_load_asap) {
        this.trackLoader.beginTrackLoading(this.store, this.tracks, this.onPointsAdded.bind(this));
    }
    this.eventEmitter.emit('scene.load', scene);
};
// The Scene has been loaded and Globe is ready to go
App.prototype.onPlayerReady = function () {
    this.eventEmitter.emit('player.ready');
    this.course.onPlayerReady(this.globe);
};
// The user has clicked play (or autoplay is enabled)
App.prototype.onPlayerStart = function () {
    Tracking.addPlayStartEvent();
    this.input.onPlayerStart(this);
    this.globes.forEach(g => g.onPlayerStart(this.store));
    if (!this.features.track_load_asap) {
        this.trackLoader.beginTrackLoading(this.store, this.tracks, this.onPointsAdded.bind(this));
    }
};
// All resources needed for the scene to begin have been loaded
App.prototype.onPlayerPlaying = function () {
    Tracking.addPlayingEvent(this.store.getState());
    this.terrainProfile.onPlayerPlaying(this.store);
    if (this.options.startWhenReady)
        this.store.dispatch(startPlaying());
    this.eventEmitter.emit('player.playing');
};
App.prototype.onClick = function () {
    // Unhide the controls for mobile
    if (this.store.getState().layout.isMobile) {
        this.store.dispatch(toggleControls(this.store.getState().layout.showControls));
    }
};
App.prototype.onActivityAdded = function (newActivity) {
    this.tracks.addTrack(newActivity.activityId, newActivity.trackChannels, newActivity.numPoints);
    this.playheads.addPlayhead({ trackId: newActivity.activityId });
    this.trackLoader.onActivityAdded(newActivity, this.store, this.onPointsAdded.bind(this));
    if (!this.options.headless) {
        // Create an Avatar
        this.avatars.onActivityAdded(newActivity);
        // Add a Trail
        const options = {
            color: newActivity.color,
            bgColor: newActivity.bgColor || 'rgb(0,0,0)',
            opacity: newActivity.opacity,
            bgOpacity: newActivity.bgOpacity,
            pointCount: newActivity.numPoints,
            lodCount: newActivity.lodDepths.length,
            trailLength: newActivity.trailLength,
            show: newActivity.show,
            isAerial: newActivity.elevationCorrectionType > 1,
        };
        this.trails.create(newActivity.activityId, options);
    }
};
App.prototype.onActivityDeleted = function (activityId) {
    // Get rid of the avatar
    this.playheads.removePlayhead(activityId);
    this.tracks.removeTrack(activityId);
    // Remove any rendering elements
    if (!this.options.headless)
        this.avatars.onActivityDeleted(activityId);
    if (!this.options.headless)
        this.trails.destroy(activityId);
};
App.prototype.onPointsAdded = function (trackId, pointData) {
    const state = this.store.getState();
    const track = this.tracks.getTrack(trackId);
    const activity = state.activities[trackId];
    // Append the new points to the activity's track
    const addedCount = track.appendPoints(pointData.trackVecs);
    if (addedCount === 0)
        return;
    // Compute the latest time for which we have all required points
    if (this.store.getState().scene.activities[trackId]) {
        const lastTime = this.tracks.getTrack(trackId).time.last || activity.startTime;
        const readyTimeAbsolute = lastTime >= activity.endTime ? Infinity : lastTime;
        const readyTime = readyTimeAbsolute - state.scene.startPoint.time;
        this.store.dispatch({ type: 'SET_TRACK_READY_TIME', trackId, readyTime });
    }
    // Notify modules that there are new points in the track
    this.distanceMarkers.onPointsAdded(this.tracks);
    this.media.onPointsAdded(track);
    // Send the new points to the GPU via Globe
    // @NOTE: If we're running in a test harness, we won't have window or a GL context
    if (!this.options.headless)
        this.trails.onPointsAdded(trackId, pointData.vertices);
    this.eventEmitter.emit('track.pointsAdded', trackId, pointData);
};
App.prototype.onActiveTrackFinished = function () {
    const state = this.store.getState();
    if (state.scene.live) {
        // Slow down to 1x so that we don't run past our points
        this.store.dispatch(setClockSpeed(1));
    }
    else if (state.scene.segments.length === 0) {
        // If a user has specified segments, don't interfere by picking a new active track
        const activities = state.activities;
        // Select next track from those that have not yet ended
        const liveTracks = Object.keys(activities).filter(tid => activities[tid].focusable && activities[tid].endTime > state.clock.time);
        // Pick track with the earliest start time, in case they haven't started yet
        const nextTrack = liveTracks.reduce((acc, cur) => (!acc || activities[cur].startTime < activities[acc].startTime ? cur : acc), undefined);
        // If there is another valid track, switch to it
        if (nextTrack)
            this.store.dispatch(setActiveActivity(nextTrack));
    }
};
App.prototype.tick = function (time = 0.0) {
    this.loading.update(this, this.store.getState(), this.globe, this.store.dispatch);
    if (this.store.getState().loading.scene.complete) {
        this.update(time);
        this.globes.forEach(g => g.render(this.camera, this.store));
        this.terrainProfile.render(this.store.getState());
        this.stats.render(this.store);
    }
    return !!this.store.getState().fatalError;
};
App.prototype.update = function (time) {
    this.clock.update(time, this.eventEmitter, this.store);
    this.timeWarp.update(this.store);
    this.ui.update(this.store.dispatch);
    this.segments.update(this.store);
    this.playheads.update(this.store.getState().clock.time, this.tracks, this.course);
    this.events.update(this);
    this.input.update(this.store.getState());
    this.camera.update(this.store, this.clock, this.eventEmitter);
    this.terrainProfile.update(this.store.getState());
    this.distanceMarkers.update(this.camera, this.tracks, this.store.getState());
    this.tracks.update(this.store.dispatch);
    this.stats.update(this.store, this.tracks, this.playheads);
    this.course.update(this.store);
    this.trails.update(this.store);
    this.media.update();
    this.labels.update(this.globe);
    this.avatars.update(this.store);
    this.hud.update(this.store.getState(), this.globe, this.camera);
    // Detect if our currently focused activity has ended
    const state = this.store.getState();
    const activeActivity = state.activities[state.tracks.activeTrack];
    const trackEndTime = activeActivity ? activeActivity.endTime : undefined;
    const trackEndDelay = state.scene.live ? 0 : DELAY_ACTIVE_END_MS;
    if (state.clock.time >= trackEndTime + trackEndDelay * state.clock.speed) {
        this.onActiveTrackFinished();
    }
    if (state.clock.time >= state.clock.stopTime + DELAY_EXIT_MS * state.clock.speed) {
        this.store.dispatch({ type: 'SET_MODAL', modalType: 'SharingControl' });
        this.store.dispatch({ type: 'STOP_PLAYING' });
    }
    else if (state.modal.modalType === 'SharingControl') {
        this.store.dispatch({ type: 'CLEAR_MODAL' });
        this.store.dispatch({ type: 'START_PLAYING' });
    }
};
